import {
  LINE_ITEM_TYPES,
  LINE_ITEM_TYPE_LABELS,
  toLineItemType,
  type LineItemType,
} from '../constants/line-item-types';

/**
 * Per-tenant line item preferences: which of the canonical types this company
 * has chosen NOT to offer.
 *
 * The vocabulary stays the 22-value enum everywhere — a hidden type is still a
 * legal value the database accepts, still labels correctly on an old ticket,
 * and still pays if a rate exists for it. Hiding only removes it from the
 * pickers and the pay-rate grid, so a casing crew that never runs a wash
 * truck is not scrolling past it on every line.
 *
 * Both endpoints are read defensively. The API's reads come back camelCase,
 * its writes take snake_case, and this endpoint has been seen in both, so
 * every key here is tried in both spellings.
 */

export interface LineItemPrefs {
  /** Types this company hides from pickers and the pay-rate grid. */
  hidden: LineItemType[];
}

/**
 * One hidden type the save response reports as still in use. The API measures
 * two things (`usageForTypes` in line-item-prefs.service.ts): pay-rate rows on
 * the type for this company, and live ticket line items on it. Either alone
 * makes the type "in use".
 *
 * A count is `null` when the API named the type but sent no number for that
 * measure — a name with no number is still a warning, and inventing `0` there
 * would read as "none".
 */
export interface HiddenTypeInUse {
  type: LineItemType;
  /** employee_pay_rates rows on this type. `payRateCount` on the wire. */
  payRateCount: number | null;
  /** live ticket_line_items rows on this type. `lineItemCount` on the wire. */
  lineItemCount: number | null;
}

export interface LineItemPrefsSaveResult extends LineItemPrefs {
  /** Hidden types the API found still in use. Empty when it reported none. */
  inUse: HiddenTypeInUse[];
}

const readKey = (obj: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (k in obj && obj[k] !== undefined) return obj[k];
  }
  return undefined;
};

/** Narrow an arbitrary list to canonical, de-duplicated line item types. */
function toTypeList(raw: unknown): LineItemType[] {
  if (!Array.isArray(raw)) return [];
  const out: LineItemType[] = [];
  for (const entry of raw) {
    // Some shapes list objects rather than strings: `{type}` / `{lineItemType}`.
    const value =
      typeof entry === 'string'
        ? entry
        : entry && typeof entry === 'object'
          ? readKey(entry as Record<string, unknown>, 'type', 'lineItemType', 'line_item_type')
          : null;
    const canonical = toLineItemType(typeof value === 'string' ? value : null);
    if (canonical && !out.includes(canonical)) out.push(canonical);
  }
  return out;
}

/**
 * Parse the payload of GET /line-item-prefs (already unwrapped from the
 * envelope). Accepts a bare array, or an object carrying the list under any of
 * the spellings the endpoint has been seen to use. Anything unrecognised reads
 * as "nothing hidden" — the safe default, since it offers every type rather
 * than silently dropping some.
 */
export function parseLineItemPrefs(payload: unknown): LineItemPrefs {
  if (Array.isArray(payload)) return { hidden: toTypeList(payload) };
  if (!payload || typeof payload !== 'object') return { hidden: [] };
  const obj = payload as Record<string, unknown>;
  const list = readKey(
    obj,
    'hiddenTypes', 'hidden_types',
    'hiddenLineItemTypes', 'hidden_line_item_types',
    'hidden',
  );
  return { hidden: toTypeList(list) };
}

function toCount(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Parse the payload of PUT /line-item-prefs. Same hidden list as the read,
 * plus the in-use report under `stillInUse`: the newly hidden types that
 * still carry a pay rate or a live line item, with both counts. The save is
 * NOT refused on that report — the caller warns.
 *
 * Live shape, PUT on the Smith demo tenant 2026-09-02:
 *   { hidden, newlyHidden, stillInUse: [{ lineItemType, label, payRateCount, lineItemCount }] }
 */
export function parseLineItemPrefsSaveResult(payload: unknown): LineItemPrefsSaveResult {
  const { hidden } = parseLineItemPrefs(payload);
  const obj = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : {};
  const rawInUse = readKey(
    obj,
    'inUse', 'in_use',
    'stillInUse', 'still_in_use',
    'hiddenInUse', 'hidden_in_use',
    'warnings',
  );
  const inUse: HiddenTypeInUse[] = [];
  if (Array.isArray(rawInUse)) {
    for (const entry of rawInUse) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as Record<string, unknown>;
      const type = toLineItemType(String(readKey(e, 'type', 'lineItemType', 'line_item_type') ?? ''));
      if (!type) continue;
      inUse.push({
        type,
        payRateCount: toCount(readKey(e, 'payRateCount', 'pay_rate_count')),
        lineItemCount: toCount(readKey(e, 'lineItemCount', 'line_item_count', 'count')),
      });
    }
  }
  return { hidden, inUse };
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/**
 * The sentence the settings page shows for the in-use report. One clause per
 * type; each non-zero count the API sent, named, in parentheses. A type the
 * API named with no usable count still appears — bare — because the API only
 * lists a type there when something is on it.
 */
export function describeHiddenInUse(inUse: HiddenTypeInUse[]): string {
  return inUse
    .map(({ type, payRateCount, lineItemCount }) => {
      const parts: string[] = [];
      if (lineItemCount !== null && lineItemCount > 0) parts.push(plural(lineItemCount, 'line item'));
      if (payRateCount !== null && payRateCount > 0) parts.push(plural(payRateCount, 'pay rate'));
      const label = LINE_ITEM_TYPE_LABELS[type];
      return parts.length ? `${label} (${parts.join(', ')})` : label;
    })
    .join(', ');
}

/**
 * The types a picker offers: everything not hidden, plus the value the row
 * already holds. A `<select>` cannot show a value it has no option for — it
 * displays the first option while state keeps the real one — so a line item
 * typed before its type was hidden must still see its own type in the list.
 */
export function visibleLineItemTypes(
  hidden: ReadonlySet<LineItemType>,
  current?: string | null,
): LineItemType[] {
  const keep = toLineItemType(current);
  return LINE_ITEM_TYPES.filter(t => !hidden.has(t) || t === keep);
}

/** `visibleLineItemTypes`, shaped for `<option>` rendering. */
export function visibleLineItemTypeOptions(
  hidden: ReadonlySet<LineItemType>,
  current?: string | null,
): { value: LineItemType; label: string }[] {
  return visibleLineItemTypes(hidden, current).map(value => ({
    value,
    label: LINE_ITEM_TYPE_LABELS[value],
  }));
}

/**
 * The rows the pay-rate grid shows: every type not hidden, plus any hidden
 * type that already carries a rate for this worker. A tenant can hide a type
 * from the pickers; it cannot thereby lose sight of money it is paying on.
 * The caller decides what "has a rate" means for its own row shape.
 */
export function payRateGridTypes(
  hidden: ReadonlySet<LineItemType>,
  hasRate: (type: LineItemType) => boolean,
): LineItemType[] {
  return LINE_ITEM_TYPES.filter(t => !hidden.has(t) || hasRate(t));
}
